import React, { createContext, useContext, useReducer, ReactNode, useEffect } from "react";
import { apiClient, userApiClient } from "../../api/HttpClient"; // Import apiClient

// Define the state interface
interface User {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  avatar?: string;
}

interface UserState {
  role: string | null;
  hostID: string | null;
  propertyID: string | null;
  organization_id: string | null; // New field
  organization_role: string | null; // New field
  organization_is_verified: boolean; // New field
  walletBalance: number | null;
  walletTransactions: any[];
  paymentLoading: boolean;
  paymentError: string | null;
  paymentSuccess: boolean;
  razorpayOrderId: string | null;
  razorpayAmount: number | null;
  user: User | null; // Add user object
}

// Define action types
type UserAction =
  | { type: "SET_ROLE"; payload: string | null }
  | { type: "SET_HOST_ID"; payload: string | null }
  | { type: "SET_PROPERTY_ID"; payload: string | null }
  | { type: "SET_ORGANIZATION_ID"; payload: string | null } // New action type
  | { type: "SET_ORGANIZATION_ROLE"; payload: string | null } // New action type
  | { type: "SET_WALLET_BALANCE"; payload: number | null }
  | { type: "SET_WALLET_TRANSACTIONS"; payload: any[] }
  | { type: "SET_PAYMENT_LOADING"; payload: boolean }
  | { type: "SET_PAYMENT_ERROR"; payload: string | null }
  | { type: "SET_PAYMENT_SUCCESS"; payload: boolean }
  | { type: "SET_RAZORPAY_ORDER_ID"; payload: string | null }
  | { type: "SET_RAZORPAY_AMOUNT"; payload: number | null }
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_ORGANIZATION_VERIFIED"; payload: boolean } // New action type
  | { type: "RESET_USER" }
  | { type: "RESET_PAYMENT_STATE" };

// Retrieve stored state from localStorage
const getInitialState = (): UserState => {
  const storedState = localStorage.getItem("userState");
  return storedState
    ? JSON.parse(storedState)
    : {
        role: null,
        hostID: null,
        propertyID: null,
        organization_id: null, // Initialize new field
        organization_role: null, // Initialize new field
        organization_is_verified: false, // Initialize new field
        walletBalance: null,
        walletTransactions: [],
        paymentLoading: false,
        paymentError: null,
        paymentSuccess: false,
        razorpayOrderId: null,
        razorpayAmount: null,
        user: null,
      };
};

// Initial state (fallback)
const initialState: UserState = {
  role: null,
  hostID: null,
  propertyID: null,
  organization_id: null, // Initialize new field
  organization_role: null, // Initialize new field
  organization_is_verified: false, // Initialize new field
  walletBalance: null,
  walletTransactions: [],
  paymentLoading: false,
  paymentError: null,
  paymentSuccess: false,
  razorpayOrderId: null,
  razorpayAmount: null,
  user: null,
};

// Reducer function
const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case "SET_ROLE":
      return { ...state, role: action.payload };
    case "SET_HOST_ID":
      return { ...state, hostID: action.payload };
    case "SET_PROPERTY_ID":
      return { ...state, propertyID: action.payload };
    case "SET_ORGANIZATION_ID":
      return { ...state, organization_id: action.payload };
    case "SET_ORGANIZATION_ROLE":
      return { ...state, organization_role: action.payload };
    case "SET_WALLET_BALANCE":
      return { ...state, walletBalance: action.payload };
    case "SET_WALLET_TRANSACTIONS":
      return { ...state, walletTransactions: action.payload };
    case "SET_PAYMENT_LOADING":
      return { ...state, paymentLoading: action.payload };
    case "SET_PAYMENT_ERROR":
      return { ...state, paymentError: action.payload, paymentSuccess: false };
    case "SET_PAYMENT_SUCCESS":
      return { ...state, paymentSuccess: action.payload, paymentError: null };
    case "SET_RAZORPAY_ORDER_ID":
      return { ...state, razorpayOrderId: action.payload };
    case "SET_RAZORPAY_AMOUNT":
      return { ...state, razorpayAmount: action.payload };
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_ORGANIZATION_VERIFIED":
        return { ...state, organization_is_verified: action.payload };
    case "RESET_PAYMENT_STATE":
      return {
        ...state,
        paymentLoading: false,
        paymentError: null,
        paymentSuccess: false,
        razorpayOrderId: null,
      };
    case "RESET_USER":
      localStorage.removeItem("userState"); // Clear userState from localStorage on logout
      return {
        role: null,
        hostID: null,
        propertyID: null,
        organization_id: null, // Clear organization_id on logout
        organization_role: null, // Clear organization_role on logout
        organization_is_verified: false,
        walletBalance: null,
        walletTransactions: [],
        paymentLoading: false,
        paymentError: null,
        paymentSuccess: false,
        razorpayOrderId: null,
        razorpayAmount: null,
        user: null,
      };
    default:
      throw new Error(`Unhandled action type: ${action}`);
  }
};

// Create the UserContext
const UserContext = createContext<{
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
} | undefined>(undefined);

// Create the provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState, getInitialState);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("userState", JSON.stringify(state));
  }, [state]);

  return (
    <UserContext.Provider value={{ state, dispatch }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the UserContext
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

// Async helper functions (similar to Redux Thunks)
export const createRazorpayOrder = async (dispatch: React.Dispatch<UserAction>, bookingId: string) => {
  dispatch({ type: "SET_PAYMENT_LOADING", payload: true });
  dispatch({ type: "RESET_PAYMENT_STATE" });
  try {
    const client = await apiClient;
    const response = await client.createOrder(bookingId);
    dispatch({ type: "SET_RAZORPAY_ORDER_ID", payload: response.order_id });
    dispatch({ type: "SET_RAZORPAY_AMOUNT", payload: response.amount });
    return response;
  } catch (error: any) {
    dispatch({ type: "SET_PAYMENT_ERROR", payload: error.message || "Failed to create order" });
    return null;
  } finally {
    dispatch({ type: "SET_PAYMENT_LOADING", payload: false });
  }
};

export const confirmRazorpayPayment = async (dispatch: React.Dispatch<UserAction>, paymentData: { razorpay_payment_id: string, razorpay_order_id: string, razorpay_signature: string, booking_id: string, first_name?: string, last_name?: string, email?: string, phone_number?: string }) => {
  dispatch({ type: "SET_PAYMENT_LOADING", payload: true });
  try {
    const client = await apiClient;
    await client.confirmPayment(paymentData);
    dispatch({ type: "SET_PAYMENT_SUCCESS", payload: true });
  } catch (error: any) {
    console.error("[confirmRazorpayPayment] Backend error:", error);
    const msg = error.message || "Failed to confirm payment";
    // Store error so PaymentFailure page can display it for diagnosis
    try { localStorage.setItem("payment_error_reason", msg); } catch (_) {}
    dispatch({ type: "SET_PAYMENT_ERROR", payload: msg });
  } finally {
    dispatch({ type: "SET_PAYMENT_LOADING", payload: false });
  }
};

export const fetchWalletData = async (dispatch: React.Dispatch<UserAction>) => {
  dispatch({ type: "SET_PAYMENT_LOADING", payload: true }); // Reusing paymentLoading for wallet for simplicity
  try {
    const client = await userApiClient;
    const response = await client.getWalletData();
    dispatch({ type: "SET_WALLET_BALANCE", payload: response.balance });
  } catch (error: any) {
    console.error("Failed to fetch wallet data:", error);
    // Optionally dispatch an error state for wallet
  } finally {
    dispatch({ type: "SET_PAYMENT_LOADING", payload: false });
  }
};

export const fetchWalletTransactions = async (dispatch: React.Dispatch<UserAction>) => {
  dispatch({ type: "SET_PAYMENT_LOADING", payload: true }); // Reusing paymentLoading for wallet for simplicity
  try {
    const client = await userApiClient;
    const response = await client.getWalletTransactions();
    dispatch({ type: "SET_WALLET_TRANSACTIONS", payload: response });
  } catch (error: any) {
    console.error("Failed to fetch wallet transactions:", error);
    // Optionally dispatch an error state for wallet
  } finally {
    dispatch({ type: "SET_PAYMENT_LOADING", payload: false });
  }
};